import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { CLIENT_VALUATIONS_CACHE_SEC } from "#app/api/clients/atonValuationAggregateFetch";
import type { ClientValuationSource } from "#app/api/clients/buildClientValuationsIndex";
import {
    buildIndexFromManagerValuations,
    clientValuationsApi,
} from "#app/api/clients/clientValuationsRtk";
import { selectManagerValuationsQuery } from "#app/store/RTK/selectors/clientValuationsSelect";
import {
    setClientValuationsIndex,
    setClientValuationsPreloadStatus,
} from "#app/store/RTK/slices/clientValuationsSlice";

export type UseManagerValuationsParams = {
    managerId: number | undefined;
    clients: ClientValuationSource[];
    /** Clients page: пишет `clientValuationsSlice`. Funnel preload — только RTK-кэш. */
    applyToSlice?: boolean;
};

function isCacheFresh(fetchedAt: number | undefined): boolean {
    if (!fetchedAt) {
        return false;
    }
    return Date.now() - fetchedAt < CLIENT_VALUATIONS_CACHE_SEC * 1000;
}

/**
 * Bulk `users_today_valuation` для раздела Clients.
 * Main thread RTK Query → `buildClientValuationsIndex` (без worker: auth/unwrap надёжнее).
 */
export function useManagerValuations({
    managerId,
    clients,
    applyToSlice = false,
}: UseManagerValuationsParams): void {
    const dispatch = useDispatch<any>();
    const requestSeqRef = useRef(0);
    const clientsKey = useMemo(
        () => clients.map((client) => client.id).filter(Boolean).join(","),
        [clients]
    );

    const queryState = useSelector((state: any) =>
        selectManagerValuationsQuery(state, managerId)
    );

    const applyIndex = useCallback(
        (rows: unknown[], fetchedAt: number) => {
            if (!applyToSlice || !managerId || clients.length === 0) {
                return;
            }
            const bulk = {
                rows: rows as never[],
                fetchedAt,
            };
            const byClientId = buildIndexFromManagerValuations(clients, bulk);
            dispatch(
                setClientValuationsIndex({
                    managerId,
                    fetchedAt,
                    byClientId,
                })
            );
        },
        [applyToSlice, clients, dispatch, managerId]
    );

    const loadFromRtk = useCallback(() => {
        if (!managerId) {
            return;
        }

        const requestSeq = ++requestSeqRef.current;
        if (applyToSlice) {
            dispatch(setClientValuationsPreloadStatus("loading"));
        }

        void dispatch(
            clientValuationsApi.endpoints.getManagerTodayValuations.initiate(
                { managerId },
                { subscribe: false }
            )
        )
            .unwrap()
            .then((bulk) => {
                if (requestSeq !== requestSeqRef.current) {
                    return;
                }
                applyIndex(bulk.rows, bulk.fetchedAt);
            })
            .catch(() => {
                if (requestSeq === requestSeqRef.current && applyToSlice) {
                    dispatch(setClientValuationsPreloadStatus("error"));
                }
            });
    }, [applyIndex, applyToSlice, dispatch, managerId]);

    useEffect(() => {
        if (!managerId) {
            return;
        }

        const cached = queryState?.data;
        if (cached && isCacheFresh(cached.fetchedAt)) {
            applyIndex(cached.rows, cached.fetchedAt);
            return;
        }

        loadFromRtk();
    }, [applyIndex, loadFromRtk, managerId, queryState?.data]);

    useEffect(() => {
        if (!applyToSlice || !managerId || clients.length === 0) {
            return;
        }
        const cached = queryState?.data;
        if (!cached || !isCacheFresh(cached.fetchedAt)) {
            return;
        }
        applyIndex(cached.rows, cached.fetchedAt);
    }, [applyIndex, applyToSlice, clients.length, clientsKey, managerId, queryState?.data]);
}

/** Прогрев RTK-кэша в фоне (воронка). Без записи в UI-slices. */
export function prefetchManagerValuations(
    dispatch: (action: unknown) => void,
    managerId: number
): void {
    void dispatch(
        clientValuationsApi.endpoints.getManagerTodayValuations.initiate(
            { managerId },
            { subscribe: false }
        )
    );
}
